'use client';

import { withBase } from '../lib/brand';
import React from 'react';
import Link from 'next/link';
import { useCaPreview } from '../lib/caPreview';

// Plain asset path (manually prefixed — the app uses assetPrefix + rewrites,
// not basePath, so static images and internal hrefs carry the prefix).
const IMG = withBase('/images/hire');
const HIRE_HREF = withBase('/campervan-hire');

// GA4 promotion-click event (standard for merchandising panels like this).
function trackPromoClick() {
  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  w.gtag?.('event', 'select_promotion', {
    creative_name: 'van_hire_panel',
    creative_slot: 'results_list_top',
    promotion_id: 'campervan_hire',
    promotion_name: 'Campervan & 4WD hire',
  });
}

/**
 * Van-hire promo panel shown at the top of the campsite results list.
 * Links through to the /campervan-hire fleet page and reuses the same owned,
 * Unsplash-licensed hero photography as that page for visual continuity.
 * Charcoal/blue Camping Australia brand tokens; Poppins headline (600 is the
 * lightest Poppins weight loaded in layout.tsx, so it renders true, not faux).
 *
 * `query` (e.g. "?from=2026-08-01&to=2026-08-10") carries trip context so the
 * fleet search opens pre-filled with the planned dates.
 */
export default function VanHirePromo({ query }: { query?: string }) {
  const preview = useCaPreview();
  if (preview) {
    // ca_preview: a row in the results list, in the list cards' own form
    // (photo left, charcoal type on white), not a dark ad unit dropped in.
    return (
      <Link
        href={query ? `${HIRE_HREF}${query}` : HIRE_HREF}
        onClick={trackPromoClick}
        className="ca-hire-row"
        aria-label="Campervan and 4WD hire, browse the fleet"
      >
        <span className="ca-hire-row__img">
          {/* a 6KB square cut: the 1500px original lost the race to the list photos on phones and left a bare well */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={`${IMG}/troopy-wa-beach-thumb.webp`} alt="" width={150} height={150} loading="eager" fetchPriority="high" decoding="async" />
        </span>
        <span className="ca-hire-row__body">
          {/* The campsite cards' own type: title, then a meta line in the
              location's place, then one 36px button on the bottom edge. */}
          <span className="ca-hire-row__title">No van? Hire one for the trip.</span>
          <span className="ca-hire-row__meta">Campervan &amp; 4WD hire, Australia-wide</span>
          <span className="ca-pill-link">Browse the fleet</span>
        </span>
      </Link>
    );
  }
  return (
    <Link
      href={query ? `${HIRE_HREF}${query}` : HIRE_HREF}
      onClick={trackPromoClick}
      className="group relative block overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/5 transition-shadow duration-300 hover:shadow-lg"
      aria-label="Campervan and 4WD hire — browse the fleet"
    >
      {/* Photo */}
      <div className="relative h-44 w-full sm:h-52">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={`${IMG}/troopy-wa-beach.jpg`}
          alt="A 4WD Land Cruiser on a bright, empty Western Australian beach"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          style={{ objectPosition: 'center 62%' }}
          loading="lazy"
        />
        {/* Legibility scrim, weighted to the bottom third so the white copy
            holds up over the bright sky/sand. Darker + starts higher than a
            typical hero overlay because the photo is high-key. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(28,30,32,0) 16%, rgba(28,30,32,0.30) 46%, rgba(28,30,32,0.66) 74%, rgba(28,30,32,0.92) 100%)',
          }}
        />
      </div>

      {/* Copy — text-shadow gives the white type a hard edge on any pixel */}
      <div
        className="absolute inset-x-0 bottom-0 p-4 text-white sm:p-5"
        style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5), 0 2px 14px rgba(0,0,0,0.45)' }}
      >
        <div className="mb-1.5 flex items-center gap-2">
          <span className="h-px w-6 bg-white/90" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white">
            Campervan &amp; 4WD hire
          </span>
        </div>
        <h3
          className="text-lg leading-tight text-white sm:text-xl"
          style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}
        >
          No van? Hire one for the trip.
        </h3>
        <p className="mt-1 max-w-[34ch] text-[13px] font-medium leading-snug text-white/95">
          Compare campervans, motorhomes &amp; 4WDs across Australia.
        </p>
        <span
          className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-[13px] font-semibold text-[#1c1e20] shadow-sm transition-colors duration-300 group-hover:bg-[#1a68d1] group-hover:text-white"
          style={{ textShadow: 'none' }}
        >
          Browse the fleet
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="transition-transform duration-300 group-hover:translate-x-0.5"
            aria-hidden="true"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </span>
      </div>
    </Link>
  );
}
